import type { ReactNode } from 'react';
import { Plus, Search, X } from 'lucide-react';

export interface SettingsListHeaderProps {
  title: string;
  /** What the search box looks for, e.g. "categories" — read as "Search categories". */
  searchNoun: string;
  search: string;
  onSearchChange: (value: string) => void;
  onAdd: () => void;
  addDisabled?: boolean;
  /** Filters shown between the search box and the Add button. */
  children?: ReactNode;
}

/** The title row of a Settings list: its name, a search box, any filters and Add. */
export function SettingsListHeader({
  title,
  searchNoun,
  search,
  onSearchChange,
  onAdd,
  addDisabled,
  children,
}: SettingsListHeaderProps) {
  return (
    <div className="p-4 border-b border-gray-200 flex items-center justify-between gap-2 flex-wrap">
      <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
      <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
        <div className="relative flex-1 sm:flex-none">
          <Search
            size={14}
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
            aria-hidden="true"
          />
          <input
            type="search"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={`Search ${searchNoun}`}
            aria-label={`Search ${searchNoun}`}
            className="w-full sm:w-56 min-h-11 sm:min-h-0 pl-8 pr-11 sm:pr-8 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          {search && (
            <button
              type="button"
              onClick={() => onSearchChange('')}
              className="absolute right-0 top-1/2 -translate-y-1/2 flex items-center justify-center min-h-11 min-w-11 sm:min-h-0 sm:min-w-0 sm:right-1 sm:p-1 text-gray-400 hover:text-gray-600"
              aria-label="Clear search"
            >
              <X size={14} />
            </button>
          )}
        </div>
        {children}
        <button
          onClick={onAdd}
          disabled={addDisabled}
          className="flex items-center gap-1 min-h-11 sm:min-h-0 px-3 py-1.5 text-sm text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
        >
          <Plus size={14} />
          Add
        </button>
      </div>
    </div>
  );
}

export interface SettingsListEmptyRowProps {
  colSpan: number;
  /** The search typed; with none the list is simply empty. */
  search: string;
  /** Plural noun, e.g. "categories". */
  noun: string;
  /** Other filters are narrowing the list, so an empty list is not an empty table. */
  filtered?: boolean;
  onClearSearch: () => void;
}

/** The row a Settings table shows when it has nothing to list, or nothing matches the search. */
export function SettingsListEmptyRow({
  colSpan,
  search,
  noun,
  filtered,
  onClearSearch,
}: SettingsListEmptyRowProps) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-4 py-8 text-center text-sm text-gray-500">
        {search.trim() ? (
          <>
            No {noun} match &ldquo;{search.trim()}&rdquo;.{' '}
            <button
              type="button"
              onClick={onClearSearch}
              className="min-h-11 sm:min-h-0 px-1 text-primary-600 hover:text-primary-700 underline"
            >
              Clear search
            </button>
          </>
        ) : filtered ? (
          `No ${noun} match the filters.`
        ) : (
          `No ${noun} yet.`
        )}
      </td>
    </tr>
  );
}
